import { StepId, OnboardingContext, STEP_ROUTES } from './types';
import { BASE_FLOWS, OFFBOARDING_FLOWS } from './flows';
import { TRANSFORMERS } from './transformers';

/**
 * Computes the complete onboarding + offboarding step sequence.
 * The questionnaire pages between onboarding and offboarding are NOT included —
 * they are managed by the existing questionnaire sub-engine.
 *
 * Flow: [...onboardingSteps, ...offboardingSteps]
 * At runtime: onboarding steps → questionnaire sub-engine → offboarding steps
 */
export function getBaseFlow(ctx: OnboardingContext): StepId[] {
  return [
    ...(BASE_FLOWS[ctx.patientOnboarding] ?? BASE_FLOWS.DEFAULT),
    ...(OFFBOARDING_FLOWS[ctx.patientOffboarding] ?? OFFBOARDING_FLOWS.DEFAULT),
  ];
}

export function computeFlow(ctx: OnboardingContext): StepId[] {
  const combined = getBaseFlow(ctx);
  return TRANSFORMERS.reduce(
    (flow, transform) => transform(flow, ctx),
    combined,
  );
}

/**
 * Determines if a given route is part of the onboarding/offboarding flow
 * (vs. the questionnaire sub-engine or entry pages).
 */
export function isFlowRoute(route: string): boolean {
  return Object.values(STEP_ROUTES).includes(route);
}

/**
 * Converts a StepId to its corresponding route URL.
 */
export function stepToRoute(stepId: StepId): string {
  return STEP_ROUTES[stepId];
}

/**
 * Converts a route URL to its corresponding StepId.
 * Returns undefined if the route is not a recognized flow step.
 */
export function routeToStepId(route: string): StepId | undefined {
  return (Object.entries(STEP_ROUTES) as [StepId, string][]).find(
    ([, r]) => r === route,
  )?.[0];
}

/**
 * Maps experiment-variant step IDs back to their base equivalents.
 * When swapExperimentVariants replaces a step (e.g. 'landing-professional-select'
 * → 'landing-professional-select-v2'), this map allows navigation fallback logic
 * to find the step's position in the un-transformed base flow.
 *
 * To add a new page-replacement experiment:
 * 1. Add the variant StepId to OnboardingStepId in types.ts
 * 2. Add its route to STEP_ROUTES in types.ts and SHARED_ROUTES in routes.ts
 * 3. Add the variant → base mapping here
 * 4. Add the swap logic in swapExperimentVariants (transformers.ts)
 * 5. Add the <Route> in AppRoutes.tsx
 */
const VARIANT_TO_BASE: Partial<Record<StepId, StepId>> = {
  // Example: 'landing-professional-select-v2': 'landing-professional-select',
};

export function resolveBaseStepId(stepId: StepId): StepId {
  return VARIANT_TO_BASE[stepId] ?? stepId;
}

/**
 * Checks if a step is an onboarding step (vs offboarding).
 */
export function isOnboardingStep(
  stepId: StepId,
): stepId is import('./types').OnboardingStepId {
  return !isOffboardingStep(stepId);
}

/**
 * Checks if a step is an offboarding step.
 */
export function isOffboardingStep(
  stepId: StepId,
): stepId is import('./types').OffboardingStepId {
  return (
    stepId === 'additional-document-upload' ||
    stepId === 'research-consent' ||
    stepId === 'confirmation' ||
    stepId === 'direct-confirmation'
  );
}
